import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import { Outlet, useNavigate, useLocation } from "react-router-dom"
import { userPermissionsSelector, activeProfileSelector } from "store/user"
import { hasModuleAccess, hasPermission, hasFullAccess } from "utils/permissions"
import { routes } from "./routes"

/**
 * PermissionGuard - Protects routes based on user permissions
 *
 * This component checks if staff_user has permission to access specific routes.
 * If they don't have permission, they're redirected to /403.
 * guide_user with full access can access all routes.
 *
 * @param {string} module - The module name (e.g., "bookings", "calendar", "clients")
 * @param {string} action - Optional action name (e.g., "view", "create", "edit")
 * @param {string} screen - Optional screen name (defaults to "{module}_main")
 * @param {React.ReactNode} children - The child components to render if permission is granted
 */
const PermissionGuard = ({ module, action = null, screen = null, children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { role } = useSelector((store) => store.user)
  const permissions = useSelector(userPermissionsSelector)
  const activeProfile = useSelector(activeProfileSelector)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    if (!role) {
      return
    }

    if (role === "guide_user") {
      setIsChecking(false)
      return
    }

    if (role === "staff_user") {
      if (hasFullAccess(activeProfile)) {
        setIsChecking(false)
        return
      }

      if (!permissions || Object.keys(permissions).length === 0) {
        return
      }

      let hasAccess = false

      if (action) {
        hasAccess = hasPermission(permissions, module, action, screen)
      } else {
        hasAccess = hasModuleAccess(permissions, module, screen)
      }

      if (!hasAccess) {
        navigate(routes.forbidden403(), {
          replace: true,
          state: { from: location.pathname }
        })
        return
      }

      setIsChecking(false)
    } else {
      setIsChecking(false)
    }
  }, [role, permissions, activeProfile, module, action, screen, navigate, location.pathname])

  if (isChecking) {
    return null
  }

  return children || <Outlet />
}

PermissionGuard.propTypes = {
  module: PropTypes.string.isRequired,
  action: PropTypes.string,
  screen: PropTypes.string,
  children: PropTypes.node
}

export default PermissionGuard
